import { useState } from 'react';
import { Link } from 'react-router';
import { CalendarPlus, Link2, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FeedDot, PageHeader, VisibilityBadge } from '@/components/app/common';
import { openCreateFeed } from '@/features/feeds/CreateFeedDialog';
import { useFeeds, useMe } from '@/lib/queries';
import { ago, plural, ROLE_LABEL } from '@/lib/format';
import { useShortcuts } from '@/lib/shortcuts';

export function Dashboard() {
  const feeds = useFeeds();
  const me = useMe();
  const [q, setQ] = useState('');
  useShortcuts({ n: () => openCreateFeed('manual') });
  document.title = 'Your calendars · Calendar Feed';

  if (feeds.isPending)
    return (
      <>
        <PageHeader title="Your calendars" />
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 sm:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
      </>
    );

  const list = feeds.data ?? [];
  if (!list.length)
    return (
      <>
        <PageHeader title="Your calendars" />
        <div className="bg-card grid grid-cols-[minmax(0,1fr)] gap-5 rounded-xl border p-6 md:p-8">
          <div>
            <h2 className="text-lg font-semibold">Welcome{me.data?.name ? `, ${me.data.name}` : ''}!</h2>
            <p className="text-muted-foreground mt-1 max-w-2xl text-sm">A calendar here is something people subscribe to. It can hold its own events and include other calendars — for example, a class calendar that includes the school calendar.</p>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
            {[
              { kind: 'manual' as const, icon: CalendarPlus, title: 'Start a new calendar', body: 'For a class, team or family. Add your own events.' },
              { kind: 'external' as const, icon: Link2, title: 'Connect an existing calendar', body: 'Paste an .ics or webcal link, like public holidays. Synced every hour.' },
            ].map((o) => (
              <button key={o.kind} type="button" onClick={() => openCreateFeed(o.kind)} className="hover:border-primary hover:bg-brand-soft focus-visible:ring-ring/50 flex flex-col items-start gap-2 rounded-xl border p-5 text-left transition-colors outline-none focus-visible:ring-[3px]">
                <span className="bg-brand-soft text-primary grid size-10 place-items-center rounded-lg">
                  <o.icon className="size-5" />
                </span>
                <span className="font-medium">{o.title}</span>
                <span className="text-muted-foreground text-sm">{o.body}</span>
              </button>
            ))}
          </div>
        </div>
      </>
    );

  const shown = list.filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageHeader
        title="Your calendars"
        actions={
          <>
            <Button variant="outline" onClick={() => openCreateFeed('external')}>
              <Link2 /> Connect a calendar
            </Button>
            <Button onClick={() => openCreateFeed('manual')} title="Shortcut: N">
              <Plus /> New calendar
            </Button>
          </>
        }
      />
      {list.length > 6 ? (
        <div className="relative mb-4 max-w-sm">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input type="search" placeholder="Filter calendars" aria-label="Filter calendars" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      ) : null}
      {shown.length ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 sm:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4">
          {shown.map((f) => (
            <Link key={f.id} to={`/feeds/${f.id}`} className="feed-card bg-card hover:border-foreground/20 focus-visible:ring-ring/50 group relative flex flex-col gap-3 overflow-hidden rounded-xl border p-5 shadow-xs transition-all outline-none hover:shadow-md focus-visible:ring-[3px]">
              <span className="absolute inset-x-0 top-0 h-1" style={{ background: f.color }} />
              <div className="flex items-center gap-2.5">
                <FeedDot color={f.color} className="size-3" />
                <span className="truncate font-semibold">{f.name}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {f.kind === 'external' ? <Badge variant="outline">External</Badge> : null}
                <VisibilityBadge v={f.visibility} />
                {f.role && f.role !== 'owner' ? <Badge variant="outline">{ROLE_LABEL[f.role]}</Badge> : null}
              </div>
              <p className="text-muted-foreground mt-auto text-xs">{[plural(f.event_count ?? 0, 'event'), f.include_count ? plural(f.include_count, 'layer') : null, `updated ${ago(f.updated_at)}`].filter(Boolean).join(' · ')}</p>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">No calendars match.</p>
      )}
    </>
  );
}
